import type {
  ProfileSpaceHistoryRecord,
  WakeLoopRequestContext,
} from "@wakeloop/schemas";
import { listProfileSpaceHistory } from "../config/profile-space-history-api";
import {
  isLocalSpaceStateCorruptResult,
  readSpaceCacheMaintenanceState,
  removeSpaceConfig,
  type SpaceCacheMaintenanceState,
  type SpaceConfig,
  toLocalSpaceStateIntegrityError,
  writeSpaceCacheMaintenanceState,
} from "./local-state";

const SPACE_CACHE_MAINTENANCE_INTERVAL_MS = 24 * 60 * 60 * 1000;
const SPACE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SPACE_CACHE_REMOVED_HISTORY_LIMIT = 500;

export type SpaceCacheMaintenanceSummary = {
  removedByRemote: number;
  removedByTtl: number;
  totalRemoved: number;
};

export type SpaceCacheMaintenanceResult = {
  localKnown: SpaceConfig[];
  summary: SpaceCacheMaintenanceSummary;
  removedByRemoteSpaceIds: string[];
};

type SpaceCacheMaintenanceDependencies = {
  listProfileSpaceHistory?: typeof listProfileSpaceHistory;
  readState?: typeof readSpaceCacheMaintenanceState;
  writeState?: typeof writeSpaceCacheMaintenanceState;
  removeSpaceConfig?: typeof removeSpaceConfig;
};

const EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY: SpaceCacheMaintenanceSummary = {
  removedByRemote: 0,
  removedByTtl: 0,
  totalRemoved: 0,
};

export async function runSpaceCacheMaintenance(input: {
  wakeLoopProfileId: string;
  requestContext: WakeLoopRequestContext;
  baseUrl: string;
  localKnown: SpaceConfig[];
  activeCloud: ProfileSpaceHistoryRecord[];
  remoteRemovedSpaceIds?: string[];
  now?: Date;
  dependencies?: SpaceCacheMaintenanceDependencies;
}): Promise<SpaceCacheMaintenanceResult> {
  const now = input.now ?? new Date();
  const readState =
    input.dependencies?.readState ?? readSpaceCacheMaintenanceState;
  const writeState =
    input.dependencies?.writeState ?? writeSpaceCacheMaintenanceState;
  const listHistory =
    input.dependencies?.listProfileSpaceHistory ?? listProfileSpaceHistory;
  const removeLocalSpace =
    input.dependencies?.removeSpaceConfig ?? removeSpaceConfig;

  const stateResult = await readState({
    wakeLoopProfileId: input.wakeLoopProfileId,
  });
  let state: SpaceCacheMaintenanceState | null = null;
  if (stateResult.kind === "ok") {
    state = stateResult.value;
  } else if (isLocalSpaceStateCorruptResult(stateResult)) {
    throw toLocalSpaceStateIntegrityError({
      operation: "reading space cache maintenance state",
      result: stateResult,
    });
  }

  if (!shouldRunSpaceCacheMaintenance({ state, now })) {
    return {
      localKnown: input.localKnown,
      summary: EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY,
      removedByRemoteSpaceIds: [],
    };
  }
  if (input.localKnown.length === 0) {
    return {
      localKnown: input.localKnown,
      summary: EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY,
      removedByRemoteSpaceIds: normalizeRemovedSpaceIds(
        input.remoteRemovedSpaceIds
      ),
    };
  }

  try {
    const activeSpaceKeys = new Set(
      input.activeCloud.map((item) => normalizeSpaceCacheKey(item.spaceId))
    );
    const remoteRemovedSpaceIds = normalizeRemovedSpaceIds(
      input.remoteRemovedSpaceIds
    );
    const remoteRemovedSpaceKeys =
      remoteRemovedSpaceIds.length > 0
        ? new Set(
            remoteRemovedSpaceIds.map((item) => normalizeSpaceCacheKey(item))
          )
        : await loadRemoteRemovedSpaceKeys({
            wakeLoopProfileId: input.wakeLoopProfileId,
            requestContext: input.requestContext,
            baseUrl: input.baseUrl,
            listHistory,
          });

    const removedByRemoteKeys = new Set<string>();
    const removedByTtlKeys = new Set<string>();
    for (const item of input.localKnown) {
      const key = normalizeSpaceCacheKey(item.space);
      if (remoteRemovedSpaceKeys.has(key)) {
        removedByRemoteKeys.add(key);
        continue;
      }
      if (activeSpaceKeys.has(key)) {
        continue;
      }
      if (isExpiredLocalSpaceCacheItem({ item, now })) {
        removedByTtlKeys.add(key);
      }
    }

    const removedKeys = new Set([
      ...removedByRemoteKeys.values(),
      ...removedByTtlKeys.values(),
    ]);
    if (removedKeys.size > 0) {
      await Promise.all(
        [...removedKeys].map(async (space) => {
          await removeLocalSpace({
            wakeLoopProfileId: input.wakeLoopProfileId,
            space,
          }).catch(() => undefined);
        })
      );
    }

    const localKnown = input.localKnown.filter(
      (item) => !removedKeys.has(normalizeSpaceCacheKey(item.space))
    );
    const summary = {
      removedByRemote: removedByRemoteKeys.size,
      removedByTtl: removedByTtlKeys.size,
      totalRemoved: removedKeys.size,
    };
    await writeMaintenanceState({
      wakeLoopProfileId: input.wakeLoopProfileId,
      now,
      summary,
      writeState,
    });
    return {
      localKnown,
      summary,
      removedByRemoteSpaceIds:
        remoteRemovedSpaceIds.length > 0
          ? remoteRemovedSpaceIds
          : [...remoteRemovedSpaceKeys.values()],
    };
  } catch {
    await writeMaintenanceState({
      wakeLoopProfileId: input.wakeLoopProfileId,
      now,
      summary: EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY,
      writeState,
    });
    return {
      localKnown: input.localKnown,
      summary: EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY,
      removedByRemoteSpaceIds: [],
    };
  }
}

export function mergeSpaceCacheMaintenanceSummary(input: {
  left: SpaceCacheMaintenanceSummary;
  right: SpaceCacheMaintenanceSummary;
}): SpaceCacheMaintenanceSummary {
  return {
    removedByRemote: input.left.removedByRemote + input.right.removedByRemote,
    removedByTtl: input.left.removedByTtl + input.right.removedByTtl,
    totalRemoved: input.left.totalRemoved + input.right.totalRemoved,
  };
}

export function createEmptySpaceCacheMaintenanceSummary(): SpaceCacheMaintenanceSummary {
  return { ...EMPTY_SPACE_CACHE_MAINTENANCE_SUMMARY };
}

async function writeMaintenanceState(input: {
  wakeLoopProfileId: string;
  now: Date;
  summary: SpaceCacheMaintenanceSummary;
  writeState: typeof writeSpaceCacheMaintenanceState;
}): Promise<void> {
  await input
    .writeState({
      wakeLoopProfileId: input.wakeLoopProfileId,
      lastRunAt: input.now.toISOString(),
      lastRemovedByRemote: input.summary.removedByRemote,
      lastRemovedByTtl: input.summary.removedByTtl,
      lastTotalRemoved: input.summary.totalRemoved,
    })
    .catch(() => undefined);
}

function shouldRunSpaceCacheMaintenance(input: {
  state: SpaceCacheMaintenanceState | null;
  now: Date;
}): boolean {
  const lastRunAt = input.state?.lastRunAt;
  if (!lastRunAt) {
    return true;
  }
  const lastRunTs = Date.parse(lastRunAt);
  if (!Number.isFinite(lastRunTs)) {
    return true;
  }
  return input.now.getTime() - lastRunTs >= SPACE_CACHE_MAINTENANCE_INTERVAL_MS;
}

async function loadRemoteRemovedSpaceKeys(input: {
  wakeLoopProfileId: string;
  requestContext: WakeLoopRequestContext;
  baseUrl: string;
  listHistory: typeof listProfileSpaceHistory;
}): Promise<Set<string>> {
  try {
    const removedHistory = await input.listHistory({
      baseUrl: input.baseUrl,
      requestContext: input.requestContext,
      wakeLoopProfileId: input.wakeLoopProfileId,
      statuses: ["removed"],
      limit: SPACE_CACHE_REMOVED_HISTORY_LIMIT,
    });
    return new Set(
      removedHistory.map((item) => normalizeSpaceCacheKey(item.spaceId))
    );
  } catch {
    return new Set();
  }
}

function isExpiredLocalSpaceCacheItem(input: {
  item: SpaceConfig;
  now: Date;
}): boolean {
  const recentActivityAt = resolveLocalSpaceRecentActivityTimestamp(input.item);
  if (recentActivityAt === null) {
    return false;
  }
  return input.now.getTime() - recentActivityAt >= SPACE_CACHE_TTL_MS;
}

function resolveLocalSpaceRecentActivityTimestamp(
  item: SpaceConfig
): number | null {
  if (
    typeof item.lastSeenTs === "number" &&
    Number.isFinite(item.lastSeenTs) &&
    item.lastSeenTs >= 0
  ) {
    return item.lastSeenTs;
  }

  for (const value of [
    item.updatedAt,
    item.spaceUpdatedAt,
    item.spaceCreatedAt,
    item.createdAt,
  ]) {
    const timestamp = parseIsoTimestamp(value);
    if (timestamp !== null) {
      return timestamp;
    }
  }

  return null;
}

function parseIsoTimestamp(value: string | undefined): number | null {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeSpaceCacheKey(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeRemovedSpaceIds(value: string[] | undefined): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized = new Map<string, string>();
  for (const item of value) {
    if (typeof item !== "string") {
      continue;
    }
    const normalizedKey = normalizeSpaceCacheKey(item);
    if (!normalizedKey || normalized.has(normalizedKey)) {
      continue;
    }
    normalized.set(normalizedKey, item);
  }
  return [...normalized.values()];
}
